<template>
  <div class="system-config-page">
    <a-card :bordered="false" class="config-card">
      <div slot="title" class="card-title">
        <a-icon type="database" />
        <div>
          <div class="title-text">MySQL 数据库备份</div>
          <div class="title-description">
            按设定时间自动生成 SQL 文件，并清理超过保留期限的历史备份
          </div>
        </div>
      </div>

      <a-spin :spinning="loading">
        <a-alert
          message="备份任务由服务端执行，关闭浏览器不会影响定时备份。"
          show-icon
          type="info"
        />

        <a-form-model
          ref="configForm"
          :label-col="{ span: 6 }"
          :model="form"
          :rules="rules"
          :wrapper-col="{ span: 12 }"
          class="config-form"
        >
          <a-form-model-item label="启用定时备份" prop="backupEnabled">
            <a-switch
              v-model="form.backupEnabled"
              :disabled="!canUpdate"
              checked-children="启用"
              un-checked-children="停用"
            />
          </a-form-model-item>

          <a-form-model-item label="每日备份时间" prop="backupTime">
            <a-time-picker
              v-model="backupTimeValue"
              :allow-clear="false"
              :disabled="!canUpdate"
              :minute-step="5"
              format="HH:mm"
              placeholder="请选择备份时间"
              @change="handleBackupTimeChange"
            />
            <div class="field-help">
              使用北京时间（UTC+8），每天在该时间执行一次。
            </div>
          </a-form-model-item>

          <a-form-model-item label="备份保留时间" prop="retentionDays">
            <a-input-number
              v-model="form.retentionDays"
              :disabled="!canUpdate"
              :max="3650"
              :min="1"
              :precision="0"
            />
            <span class="unit">天</span>
            <div class="field-help">
              备份成功后，自动删除修改时间早于该期限的本系统 SQL 备份。
            </div>
          </a-form-model-item>

          <a-form-model-item :wrapper-col="{ offset: 6, span: 12 }">
            <a-button
              v-if="canUpdate"
              :loading="saving"
              type="primary"
              @click="save"
            >
              保存配置
            </a-button>
            <a-button
              v-if="canBackupNow"
              :loading="backingUp"
              class="backup-now-button"
              @click="runBackupNow"
            >
              立即备份
            </a-button>
          </a-form-model-item>
        </a-form-model>
      </a-spin>
    </a-card>

    <a-card :bordered="false" class="status-card" title="最近一次备份">
      <a-descriptions :column="2" bordered size="small">
        <a-descriptions-item label="状态">
          <a-tag :color="status.color">{{ status.text }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="执行时间（北京时间）">
          {{ config.lastBackupAt || "尚未执行" }}
        </a-descriptions-item>
        <a-descriptions-item label="备份文件" :span="2">
          <span class="file-path">{{ config.lastBackupFile || "-" }}</span>
        </a-descriptions-item>
        <a-descriptions-item label="执行结果" :span="2">
          {{ config.lastBackupMessage || "-" }}
        </a-descriptions-item>
        <a-descriptions-item label="配置更新时间（北京时间）" :span="2">
          {{ config.updateTime || "-" }}
        </a-descriptions-item>
      </a-descriptions>
    </a-card>
  </div>
</template>

<script>
import moment from "moment"
import { message } from "ant-design-vue"
import {
  backupNow,
  getSystemConfig,
  updateSystemConfig
} from "@/api/system/config"

export default {
  name: "SystemConfig",
  data() {
    return {
      loading: false,
      saving: false,
      backingUp: false,
      backupTimeValue: moment("02:00", "HH:mm"),
      form: {
        backupEnabled: false,
        backupTime: "02:00",
        retentionDays: 7
      },
      config: {},
      rules: {
        backupTime: [
          { required: true, message: "请选择每日备份时间", trigger: "change" }
        ],
        retentionDays: [
          { required: true, message: "请输入备份保留天数", trigger: "change" }
        ]
      }
    }
  },
  computed: {
    permissionCodes() {
      return (this.$route.meta && this.$route.meta.code) || []
    },
    canUpdate() {
      return this.permissionCodes.includes("修改系统配置")
    },
    canBackupNow() {
      return this.permissionCodes.includes("立即备份数据库")
    },
    status() {
      const statusMap = {
        never: { color: "default", text: "尚未执行" },
        running: { color: "processing", text: "执行中" },
        success: { color: "success", text: "成功" },
        failed: { color: "error", text: "失败" }
      }
      return statusMap[this.config.lastBackupStatus] || statusMap.never
    }
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    async loadConfig() {
      this.loading = true
      try {
        const response = await getSystemConfig()
        if (response.code === 0) {
          this.applyConfig(response.result)
        }
      } finally {
        this.loading = false
      }
    },
    applyConfig(config) {
      this.config = config || {}
      this.form = {
        backupEnabled: Boolean(config.backupEnabled),
        backupTime: config.backupTime || "02:00",
        retentionDays: Number(config.retentionDays || 7)
      }
      this.backupTimeValue = moment(this.form.backupTime, "HH:mm")
    },
    handleBackupTimeChange(value) {
      this.form.backupTime = value ? value.format("HH:mm") : ""
    },
    save() {
      this.$refs.configForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const response = await updateSystemConfig({ ...this.form })
          if (response.code === 0) {
            this.applyConfig(response.result)
            message.success("系统配置已保存")
          }
        } finally {
          this.saving = false
        }
      })
    },
    async runBackupNow() {
      this.backingUp = true
      try {
        const response = await backupNow()
        if (response.code === 0) {
          message.success("数据库备份完成")
          await this.loadConfig()
        }
      } finally {
        this.backingUp = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.system-config-page {
  padding: 24px;
}

.config-card,
.status-card {
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 35, 52, 0.03),
    0 6px 18px rgba(15, 35, 52, 0.04);
}

.status-card {
  margin-top: 16px;
}

.card-title {
  display: flex;
  align-items: center;

  > .anticon {
    margin-right: 12px;
    color: #1890ff;
    font-size: 22px;
  }
}

.title-text {
  color: #1a2333;
  font-size: 16px;
  font-weight: 600;
}

.title-description,
.field-help {
  margin-top: 4px;
  color: #8b97a8;
  font-size: 12px;
  font-weight: normal;
}

.config-form {
  max-width: 820px;
  margin-top: 28px;
}

.unit {
  margin-left: 8px;
}

.backup-now-button {
  margin-left: 12px;
}

.file-path {
  word-break: break-all;
}
</style>
